import type { IFooterProps, IIbarProps } from "@frontiers-vue3/public-pages";
import { useApiService } from "./useApiService";
import { useCacheService } from "~/composables/useCacheService";
import type { IJournal } from "~/types";
import type { IContentfulJournal } from "~/types/contentful/IContentfulJournal";

const uniqueJournalSlugs = (journals: IJournal[]): string[] => {
  const slugs: string[] = [];
  const seen = new Set<string>();

  for (const journal of journals) {
    const slug = journal.slug?.trim();
    if (!slug || seen.has(slug)) {
      continue;
    }
    seen.add(slug);
    slugs.push(slug);
  }

  return slugs;
};

const assertServerOnly = () => {
  if (import.meta.client) {
    throw new Error("useJournalPagesService can only be used on the server");
  }
};

export const useJournalPagesService = () => {
  assertServerOnly();

  const { journalsPagesUrl } = useRuntimeConfig().public;
  const { getFetchData } = useApiService();
  const { getFormerKey, cache, LONG_TTL, DAY_TTL } = useCacheService();

  const getIbarComponent = (journalId?: number): Promise<IIbarProps> => {
    const endpoint = journalId
      ? `${journalsPagesUrl}/components/ibar?journalId=${journalId}`
      : `${journalsPagesUrl}/components/ibar`;
    const cacheKey = journalId
      ? getFormerKey("api", "journal-pages:ibar", journalId)
      : getFormerKey("api", "journal-pages:ibar");

    return cache(
      cacheKey,
      async () => {
        try {
          return await getFetchData<IIbarProps>(endpoint);
        } catch (err: unknown) {
          throw Error(
            `Error getting the ibar with journalId '${journalId}'. Message: ${
              err instanceof Error ? err.message : err
            }`
          );
        }
      },
      LONG_TTL
    );
  };

  const getFooterComponent = (): Promise<IFooterProps> => {
    const cacheKey = getFormerKey("api", "journal-pages:footer");

    return cache(
      cacheKey,
      async () => {
        try {
          return await getFetchData<IFooterProps>(
            `${journalsPagesUrl}/components/footer`
          );
        } catch (err: unknown) {
          throw Error(
            `Error getting footer. Message: ${
              err instanceof Error ? err.message : err
            }`
          );
        }
      },
      LONG_TTL
    );
  };

  const getContentfulJournal = (
    journalSlug: string
  ): Promise<IContentfulJournal> => {
    const slug = journalSlug.trim();
    const cacheKey = getFormerKey("api", "journal-pages:journal", slug);

    return cache(
      cacheKey,
      async () => {
        try {
          return await getFetchData<IContentfulJournal>(
            `${journalsPagesUrl}/journals/${slug}`
          );
        } catch (err: unknown) {
          throw Error(
            `Error getting journal info of slug ${journalSlug}. Message: ${err instanceof Error ? err.message : err
            }`
          );
        }
      },
      DAY_TTL
    );
  };

  const getContentfulJournals = (
    journals: IJournal[]
  ): Promise<IContentfulJournal[]> =>
    Promise.all(uniqueJournalSlugs(journals).map(getContentfulJournal));

  return {
    getIbarComponent,
    getFooterComponent,
    getContentfulJournal,
    getContentfulJournals
  };
};
